import { useTheme } from '@mui/material/styles';
import { default as GoogleMapReact } from 'google-map-react';
import { ReactElement, useCallback, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { QueryJobSiteCounty } from '../../api/schema';

// A choropleth of counties: each boundary is filled by how many open job sites
// it holds, and counties with data carry a count label at their centre. The
// base map is stripped to water and place names so the county shading is the
// only thing to read.

// Must match the loader options in Common.tsx byte for byte: the Google Maps
// script loads once per page and refuses a second load with different options.
const bootstrapURLKeys = {
  key: (
    window as unknown as {
      _env_: {
        REACT_APP_GOOGLE_MAP_API_KEY: string;
      };
    }
  )._env_.REACT_APP_GOOGLE_MAP_API_KEY,
  libraries: ['drawing', 'geometry'],
  version: '3.59',
};

// google-map-react sizes its canvas as 100% of this element, which only
// resolves against an explicit height, so the height is always a pixel value.
const Wrapper = styled.div`
  width: 100%;
`;

// Roads, points of interest and transit are noise at county scale. Locality
// names stay so a reader can still orient themselves inside a shaded county.
const mapStyles = [
  { featureType: 'road', stylers: [{ visibility: 'off' }] },
  { featureType: 'poi', stylers: [{ visibility: 'off' }] },
  { featureType: 'transit', stylers: [{ visibility: 'off' }] },
  { featureType: 'landscape', elementType: 'labels', stylers: [{ visibility: 'off' }] },
  { featureType: 'administrative.neighborhood', stylers: [{ visibility: 'off' }] },
  { featureType: 'administrative.land_parcel', stylers: [{ visibility: 'off' }] },
  { featureType: 'administrative.locality', elementType: 'labels.text', stylers: [{ visibility: 'simplified' }] },
];

type CountyMapProps = {
  counties: QueryJobSiteCounty[];
  height?: number;
};

type FeatureValues = { [key: string]: unknown };

const num = (value?: number | null) => Math.round(value ?? 0).toLocaleString();

export const CountyMap = ({ counties, height = 480 }: CountyMapProps): ReactElement => {
  const theme = useTheme();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const mapRef = useRef<any>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const mapsRef = useRef<any>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const labelsRef = useRef<any[]>([]);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const infoWindowRef = useRef<any>(null);
  const fill = theme.palette.primary.main;

  const draw = useCallback(() => {
    const map = mapRef.current;
    const maps = mapsRef.current;
    if (!map || !maps) {
      return;
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    map.data.forEach((feature: any) => map.data.remove(feature));
    labelsRef.current.forEach((label) => label.setMap(null));
    labelsRef.current = [];

    const rows = counties.filter((county) => county.geometry);
    if (!rows.length) {
      return;
    }
    const most = Math.max(1, ...rows.map((county) => county.open_sites ?? 0));
    const bounds = new maps.LatLngBounds();

    rows.forEach((county) => {
      const feature = {
        type: 'Feature',
        geometry: JSON.parse(county.geometry as string),
        properties: {
          county: county.county,
          state: county.state,
          open_sites: county.open_sites ?? 0,
          equipment_count: county.equipment_count ?? 0,
          avg_days_open: county.avg_days_open,
        },
      };
      const [added] = map.data.addGeoJson(feature);
      const featureBounds = new maps.LatLngBounds();
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      added.getGeometry().forEachLatLng((latLng: any) => {
        featureBounds.extend(latLng);
        bounds.extend(latLng);
      });
      if ((county.open_sites ?? 0) > 0) {
        labelsRef.current.push(
          new maps.Marker({
            map,
            position: featureBounds.getCenter(),
            clickable: false,
            icon: { path: maps.SymbolPath.CIRCLE, scale: 0 },
            label: { text: String(county.open_sites), color: theme.palette.common.white, fontWeight: 'bold', fontSize: '14px' },
          }),
        );
      }
    });

    // Shading scales with the share of the busiest county so one dominant
    // county does not wash the rest out to the same pale tint. Counties with
    // nothing on them are drawn as outlines only, in the same light grey as
    // the base map, so the eye goes straight to the shaded ones.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    map.data.setStyle((feature: any) => {
      const openSites = feature.getProperty('open_sites') as number;
      if (!openSites) {
        return {
          fillColor: theme.palette.common.white,
          fillOpacity: 0.7,
          strokeColor: theme.palette.grey[400],
          strokeOpacity: 0.9,
          strokeWeight: 1,
        };
      }
      return {
        fillColor: fill,
        fillOpacity: 0.25 + 0.6 * (openSites / most),
        strokeColor: fill,
        strokeOpacity: 0.8,
        strokeWeight: 1,
      };
    });
    map.fitBounds(bounds, 16);
  }, [counties, fill, theme.palette.common.white, theme.palette.grey]);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleApiLoaded = ({ map, maps }: { map: any; maps: any }) => {
    mapRef.current = map;
    mapsRef.current = maps;
    infoWindowRef.current = new maps.InfoWindow();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    map.data.addListener('click', (event: any) => {
      const values = {} as FeatureValues;
      ['county', 'state', 'open_sites', 'equipment_count', 'avg_days_open'].forEach((key) => {
        values[key] = event.feature.getProperty(key);
      });
      const avg = values.avg_days_open as number | null | undefined;
      infoWindowRef.current.setContent(
        `<div><strong>${values.county}, ${values.state}</strong>` +
          `<div>Job Sites: ${num(values.open_sites as number)}</div>` +
          `<div>Equipment: ${num(values.equipment_count as number)}</div>` +
          `<div>Avg Days Open: ${avg === null || avg === undefined ? '' : num(avg)}</div></div>`,
      );
      infoWindowRef.current.setPosition(event.latLng);
      infoWindowRef.current.open(map);
    });
    draw();
  };

  // Redraw (and so refit the counties) when they change or the map is resized.
  useEffect(() => {
    draw();
  }, [draw, height]);

  return (
    <Wrapper style={{ height }}>
      <GoogleMapReact
        bootstrapURLKeys={bootstrapURLKeys}
        defaultCenter={{ lat: 40.75, lng: -73.9 }}
        defaultZoom={8}
        yesIWantToUseGoogleMapApiInternals
        onGoogleApiLoaded={handleApiLoaded}
        options={{
          styles: mapStyles,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: true,
          clickableIcons: false,
        }}
      />
    </Wrapper>
  );
};
